import styled from 'styled-components';

/**
 * Heat ramp derived from the ChalkTalk primary palette
 * (chalktalk-react/Theme/ThemeObject/MUIDefaultTheme.tsx). Single-hue
 * sequential, so it is colourblind safe.
 *
 * Index 0 is NOT a pale tint of the ramp. It is a warm neutral, distinct in
 * both hue and lightness, so a gap reads as a gap rather than as a low value.
 * Making absence visible is the point of the report.
 */
export const ZERO_COLOUR = '#EFE9E2';
// #8A8377 measured 3.11:1 against ZERO_COLOUR at the ~11px cell text size --
// below the 4.5:1 WCAG AA minimum for normal text. #6B6558 measures 4.80:1
// against the same background (see Accountability.style.test.ts).
export const ZERO_TEXT = '#6B6558';

/**
 * A row with only zeros is grayed out, as if switched off: lighter swatches and
 * light gray text. The text is below the 4.5:1 WCAG AA level on purpose, but
 * keeps 3:1 or more so it stays readable (see Accountability.style.test.ts).
 */
export const FADED_ZERO_COLOUR = '#F7F4F0';
export const FADED_TEXT = '#878C95';

export const HEAT_LEVEL_COLOURS = [
  ZERO_COLOUR,
  '#DBE7FB',
  '#A8C4F4',
  '#6796EC',
  '#2E5BC7',
  '#1A3E96',
];

/**
 * Text colour per heat level, chosen for >= 4.5:1 contrast against that
 * level's swatch colour (WCAG AA, normal text) -- see
 * Accountability.style.test.ts for the measured ratio of every level.
 *
 * Levels 0-3 use dark text: #6796EC (level 3) only measures 2.94:1 against
 * white, so light text is wrong until level 4, where the swatch is dark
 * enough (#2E5BC7, #1A3E96) that dark text itself would fail.
 */
export const heatTextColour = (level: number): string => {
  if (level === 0) return ZERO_TEXT;
  return level >= 4 ? '#FFFFFF' : '#1A1D24';
};

/**
 * Hover tints for the row/column orientation aid below. Plain neutral grey,
 * not a pale tint of the blue heat ramp and not the zero-swatch's warm hue --
 * this is an orientation aid on a dense grid, not a selection state, and it
 * must not compete with (or be mistaken for) the ramp it sits behind.
 */
export const ROW_HOVER_TINT = '#F2F4F7';
export const COLUMN_HOVER_TINT = '#EAECF0';

/**
 * Muted text for headers, helper text and "last seen". Dark enough for WCAG AA
 * on white and on the hover tints.
 */
export const MUTED_TEXT = '#5F6673';

/** Pressed segmented-control background (white text on it). */
export const PRESSED_BACKGROUND = '#2E5BC7';

/** Same pill as the sidebar's `.beta` badge (LeftSiderBar.style.js). */
export const BETA_BACKGROUND = '#666666';

export const GridScroller = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme?.palette?.divider || '#E2E5EB'};
  border-radius: 6px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme?.palette?.primary?.main || '#2E5BC7'};
    outline-offset: 2px;
  }
`;

export const GridTable = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;

  th,
  td {
    padding: 0;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #ffffff;
    font-size: 0.68rem;
    font-weight: 500;
    letter-spacing: 0.03em;
    color: ${MUTED_TEXT};
    text-align: center;
    padding: 0.5rem 0.35rem 0.4rem;
    white-space: nowrap;
    border-bottom: 1px solid #cbd0da;
  }

  /*
   * Row hover: a dense grid (~1,000 rows x ~41 columns) rules out a
   * per-row React handler, so this is a plain CSS :hover rule -- one style
   * recalculation on the row the pointer is over, nothing re-rendered.
   *
   * Tints every td's own background, which is what shows through the 2px
   * margin around each HeatSwatch (the swatch itself, being an opaque
   * child, paints over the tint -- exactly the "thin frame" look wanted).
   *
   * This selector (2 combined classes/pseudo, 3 elements) is more specific
   * than RowHeadCell's own single-class "background: #ffffff" rule, so it
   * wins outright and overrides the sticky row header's background without
   * needing !important. Kept opaque (not rgba) so the sticky cell still
   * fully occludes horizontally-scrolled columns while its row is hovered.
   */
  tbody tr:hover td {
    background: ${ROW_HOVER_TINT};
  }

  /* A row with only zeros (data-empty) is grayed out, every text in it too. */
  tbody tr[data-empty='true'] td,
  tbody tr[data-empty='true'] td * {
    color: ${FADED_TEXT};
  }
`;

/**
 * A bucket column's <th>. Needs the same `&&` specificity-doubling trick as
 * RowHeadCell below: GridTable's own `thead th { background: #ffffff }`
 * rule is a descendant selector with two type selectors (thead, th), which
 * out-specifies a plain single-class rule even though this component's own
 * class is more specific in "author intent" -- doubling the class here
 * raises it past that without needing `!important`.
 */
export const BucketHeaderCell = styled.th<{ highlighted?: boolean }>`
  && {
    ${({ highlighted }) => highlighted && `background: ${COLUMN_HOVER_TINT};`}
  }
`;

export const RowHeadCell = styled.td<{ depth: number }>`
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  min-width: 15rem;
  white-space: nowrap;
  border-bottom: 1px solid #e2e5eb;

  /* && doubles the class to out-specify GridTable's \`th, td { padding: 0 }\`
     reset, which is a descendant selector and would otherwise win and flatten
     the hierarchy indentation. */
  && {
    padding: 0.3rem 0.75rem 0.3rem ${({ depth }) => 0.75 + depth * 1.15}rem;
  }
`;

export const HeatTd = styled.td<{ highlighted?: boolean }>`
  position: relative;
  border-bottom: 1px solid #e2e5eb;
  border-left: 1px solid #e2e5eb;
  text-align: center;

  /*
   * Column hover: \`highlighted\` is driven from React state (the hovered
   * column index, tracked in AccountabilityGrid) rather than CSS :hover, so
   * the same flag can also reach the column's <th> -- something no CSS
   * selector can do, since a \`::after\` on a <td> cannot style a <th> in a
   * different row's <thead>. The tint is a plain background on this cell's
   * own box, so it never grows outside the cell's bounds and cannot expand
   * the table's scrollable overflow the way the old absolutely-positioned
   * pseudo-element (top/bottom: ±100vh) did.
   */
  ${({ highlighted }) => highlighted && `background: ${COLUMN_HOVER_TINT};`}
`;

export const HeatSwatch = styled.span<{ level: number; thin?: boolean }>`
  display: block;
  margin: 2px;
  border-radius: 2px;
  min-width: 2.6rem;
  padding: ${({ thin }) => (thin ? '0.13rem 0' : '0.28rem 0')};
  font-size: ${({ thin }) => (thin ? '0.66rem' : '0.74rem')};
  background: ${({ level }) => HEAT_LEVEL_COLOURS[level] || ZERO_COLOUR};
  color: ${({ level }) => heatTextColour(level)};

  tr[data-empty='true'] & {
    background: ${FADED_ZERO_COLOUR};
  }
`;

export const BandLabelCell = styled(RowHeadCell)`
  font-size: 0.7rem;
  color: #4e545f;
`;

export const ToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  padding: 0;
  text-align: left;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;

  &:focus-visible {
    outline: 2px solid #2e5bc7;
    outline-offset: 2px;
  }
`;

export const LastSeen = styled.span`
  margin-left: 0.6rem;
  font-size: 0.7rem;
  color: ${MUTED_TEXT};
`;

/**
 * Muted tag after a section's name: its curriculum, and "archived". Capped so a
 * long curriculum cannot widen the frozen name column.
 */
export const SectionTag = styled.span`
  display: inline-block;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
  font-size: 0.7rem;
  color: ${MUTED_TEXT};
  white-space: nowrap;
`;

/** The Total column header. Not sticky: the name column has no fixed width to pin it to. */
export const TotalHeaderCell = styled.th`
  && {
    text-align: right;
    padding-right: 0.6rem;
    border-left: 1px solid #e2e5eb;
  }
`;

/** Total cell on an active-day row: active days in the window. */
export const TotalCell = styled.td`
  min-width: 3.25rem;
  border-bottom: 1px solid #e2e5eb;
  border-left: 1px solid #e2e5eb;
  text-align: right;
  white-space: nowrap;
  font-size: 0.72rem;
  color: #1a1d24;

  && {
    padding: 0 0.6rem;
  }
`;

/** Total cell on an activity row: how many times it happened in the window. */
export const BandTotalCell = styled(TotalCell)`
  font-weight: 600;
`;

/** A toolbar control with its caption stacked above it. */
export const ControlLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.72rem;
`;

/** Caption for a control group that is not a single form field. */
export const ControlCaption = styled.span`
  font-size: 0.72rem;
`;

/** Always-visible explanation under a toggle. */
export const HelperText = styled.span`
  display: block;
  max-width: 21rem;
  font-size: 0.68rem;
  line-height: 1.35;
  color: ${MUTED_TEXT};
`;

export const SegmentedGroup = styled.div`
  display: flex;
  width: fit-content;
  border: 1px solid #cbd0da;
  border-radius: 4px;
  overflow: hidden;
`;

/**
 * One option of a segmented control, styled from aria-pressed so look and state
 * always match. The inset two-tone focus ring shows on white and on blue.
 */
export const SegmentButton = styled.button`
  background: #ffffff;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: 0.78rem;
  padding: 0.28rem 0.6rem;
  color: #4e545f;

  & + & {
    border-left: 1px solid #cbd0da;
  }

  &[aria-pressed='true'] {
    background: ${PRESSED_BACKGROUND};
    color: #ffffff;
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px #ffffff, inset 0 0 0 4px #1a1d24;
  }
`;

/** A button that reads as an inline link (the legend's Definitions link). */
export const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: #2e5bc7;
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #2e5bc7;
    outline-offset: 2px;
  }
`;

export const BetaBadge = styled.span`
  display: inline-block;
  background: ${BETA_BACKGROUND};
  border-radius: 18px;
  color: #ffffff;
  font: normal normal normal 9px/12px Roboto, sans-serif;
  letter-spacing: 0.45px;
  padding: 3px 7px;
`;
